import { useCallback, useEffect, useState } from "react";
import {
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import ThumbDownIcon from "@mui/icons-material/ThumbDown";
import moment from "moment";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import MDBox from "components/MDBox";
import {
  DeleteAppReviewFeedbackCall,
  GetAppReviewFeedbackCall,
} from "../../redux/api/appReviewFeedback.api";

export default function AppFeedbackPage() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const load = useCallback(async () => {
    setLoading(true);
    const res = await GetAppReviewFeedbackCall({
      limit: rowsPerPage,
      offset: page * rowsPerPage,
    });
    setRows(res.rows || []);
    setTotal(res.total || 0);
    setLoading(false);
  }, [page, rowsPerPage]);

  useEffect(() => {
    void load();
  }, [load]);

  const onDelete = async (id) => {
    await DeleteAppReviewFeedbackCall(id);
    void load();
  };

  const upCount = rows.filter((r) => r.sentiment === "up").length;
  const downCount = rows.filter((r) => r.sentiment === "down").length;

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3} px={2}>
        <Stack spacing={1} mb={3}>
          <Typography variant="h4" fontWeight={800}>
            App review feedback
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Thumbs up / down from the in-app review prompt (cat modal).
          </Typography>
        </Stack>

        <Stack direction="row" spacing={1} mb={2}>
          <Chip
            icon={<ThumbUpIcon />}
            label={`Thumbs up (page): ${upCount}`}
            color="success"
            variant="outlined"
          />
          <Chip
            icon={<ThumbDownIcon />}
            label={`Thumbs down (page): ${downCount}`}
            color="error"
            variant="outlined"
          />
        </Stack>

        <Paper elevation={0} sx={{ border: 1, borderColor: "divider" }}>
          {loading ? (
            <Box py={6} display="flex" justifyContent="center">
              <CircularProgress size={32} />
            </Box>
          ) : (
            <>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell>Sentiment</TableCell>
                      <TableCell>User</TableCell>
                      <TableCell>Subscriber</TableCell>
                      <TableCell>Platform</TableCell>
                      <TableCell>Sessions</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7}>
                          <Typography color="text.secondary" py={2}>
                            No feedback yet.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      rows.map((row) => (
                        <TableRow key={row.id} hover>
                          <TableCell>
                            {moment(row.created_at).format("MMM D, YYYY HH:mm")}
                          </TableCell>
                          <TableCell>
                            {row.sentiment === "up" ? (
                              <Chip
                                size="small"
                                color="success"
                                icon={<ThumbUpIcon />}
                                label="Up"
                              />
                            ) : (
                              <Chip
                                size="small"
                                color="error"
                                icon={<ThumbDownIcon />}
                                label="Down"
                              />
                            )}
                          </TableCell>
                          <TableCell>
                            {row.user?.email ||
                              row.user_id?.slice?.(0, 8) ||
                              "—"}
                          </TableCell>
                          <TableCell>
                            {row.is_subscriber ? "Yes" : "No"}
                          </TableCell>
                          <TableCell>{row.platform || "—"}</TableCell>
                          <TableCell>{row.session_count ?? "—"}</TableCell>
                          <TableCell align="right">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => onDelete(row.id)}
                            >
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={total}
                page={page}
                onPageChange={(_, p) => setPage(p)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10));
                  setPage(0);
                }}
              />
            </>
          )}
        </Paper>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}
